import { Link } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Container } from "../layout/Container";
import SectionHeaderRow from "../shared/SectionHeaderRow";
import { cPath } from "../../constants";
import { formatDiscountOff } from "../../utils/offerDisplay";

const Section = styled.section`
  padding: 2.5rem 0;
`;

const Grid = styled.div`
  display: grid;
  gap: 1rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled(Link)`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadow.card};
  }
`;

const Exclusive = styled.span`
  font-size: 0.75rem;
  background: hsl(${({ theme }) => theme.colors.couponMaroon} / 0.1);
  color: hsl(${({ theme }) => theme.colors.couponMaroon});
  font-weight: 700;
  padding: 0.25rem 0.75rem;
  border-radius: ${({ theme }) => theme.radius.full};
`;

const Logo = styled.img`
  width: 4rem;
  height: 4rem;
  object-fit: contain;
`;

const StoreLabel = styled.span`
  font-size: 0.875rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  font-weight: 500;
`;

const SaveLine = styled.span`
  font-size: 1.25rem;
  font-weight: 800;
  color: hsl(${({ theme }) => theme.colors.foreground});
`;

const Verified = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: hsl(${({ theme }) => theme.colors.accent});
`;

const Empty = styled.p`
  margin: 0;
  text-align: center;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  font-size: 0.9375rem;
`;

export default function HomeFeaturedDeals({ coupons = [] }) {
  const { t } = useTranslation();
  const topCoupons = (coupons || [])
    .filter((c) => c.exclusive || c.verified)
    .slice(0, 3);

  if (!topCoupons.length) {
    return (
      <Section>
        <Container>
          <SectionHeaderRow
            title={t("home.featuredDeals.title")}
            to={cPath("categories")}
            linkLabel={t("home.featuredDeals.viewAll")}
            marginBottom="1.5rem"
          />
          <Empty>{t("home.featuredDeals.noDeals")}</Empty>
        </Container>
      </Section>
    );
  }

  return (
    <Section>
      <Container>
        <SectionHeaderRow
          title={t("home.featuredDeals.title")}
          to={cPath("categories")}
          linkLabel={t("home.featuredDeals.viewAll")}
          marginBottom="1.5rem"
        />
        <Grid>
          {topCoupons.map((coupon) => (
            <Card key={coupon.id} to={cPath(`coupon/${coupon.id}`)}>
              {coupon.exclusive && <Exclusive>{t("home.featuredDeals.exclusive")}</Exclusive>}
              <Logo src={coupon.storeLogo} alt={coupon.storeName} />
              <StoreLabel>{coupon.storeName}</StoreLabel>
              <SaveLine>{formatDiscountOff(coupon) || t("coupon.getDeal")}</SaveLine>
              {coupon.verified && <Verified>{t("home.featuredDeals.verified")}</Verified>}
            </Card>
          ))}
        </Grid>
      </Container>
    </Section>
  );
}
